import { ModelNameFlags } from '@era/locations-sdk';
import {
  BrandRestrictionsProfileConfiguration,
  KioskRestrictionsProfileConfiguration,
  LocationRestrictionsProfileConfiguration,
  RoomRestrictionsProfileConfiguration,
} from '@era/settings-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CategoryType } from '../shared/types';

export interface RestrictionProfileConfiguration {
  categoryType?: CategoryType;
  categoryId?: string;
  profileId: string;
  displayName: string;
  modelNames: ModelNameFlags[];
}

type AnyRestrictionProfileConfiguration =
  | BrandRestrictionsProfileConfiguration
  | LocationRestrictionsProfileConfiguration
  | RoomRestrictionsProfileConfiguration
  | KioskRestrictionsProfileConfiguration;

/**
 * Generic function to convert any restriction profile configuration to the unified format.
 * Automatically determines the category type and ID based on the input configuration.
 */
export function restrictionProfileConfigurationFrom(
  configuration: AnyRestrictionProfileConfiguration
): RestrictionProfileConfiguration {
  // Type guard functions to determine the category type
  if ('brandId' in configuration) {
    return {
      categoryType: 'Brand',
      categoryId: configuration.brandId,
      profileId: configuration.restrictionsProfilePresetId,
      displayName: configuration.displayName,
      modelNames: configuration.models,
    };
  }
  if ('locationId' in configuration) {
    return {
      categoryType: 'Location',
      categoryId: configuration.locationId,
      profileId: configuration.restrictionsProfilePresetId,
      displayName: configuration.displayName,
      modelNames: configuration.models,
    };
  }
  if ('roomId' in configuration) {
    return {
      categoryType: 'Room',
      categoryId: configuration.roomId,
      profileId: configuration.restrictionsProfilePresetId,
      displayName: configuration.displayName,
      modelNames: configuration.models,
    };
  }
  if ('kioskId' in configuration) {
    return {
      categoryType: 'Kiosk',
      categoryId: configuration.kioskId,
      profileId: configuration.restrictionsProfilePresetId,
      displayName: configuration.displayName,
      modelNames: configuration.models,
    };
  }
  throw new Error('Unknown restriction profile configuration type');
}

export type CategoryRestrictionProfileConfigurationsState = EntityState<RestrictionProfileConfiguration>;

export function selectId(configuration: RestrictionProfileConfiguration): string {
  return `${configuration.profileId}-${configuration.categoryType}-${configuration.categoryId}`;
}

export const adapter: EntityAdapter<RestrictionProfileConfiguration> =
  createEntityAdapter<RestrictionProfileConfiguration>({
    selectId,
  });

export const initialState: CategoryRestrictionProfileConfigurationsState = adapter.getInitialState();

/**
 * Partial state interface for components that use category restriction profile configurations store
 * Includes both categoryRestrictionProfileConfigurations and restrictionProfiles feature states
 */
export interface CategoryRestrictionProfileConfigurationsPartialState {
  categoryRestrictionProfileConfigurations: CategoryRestrictionProfileConfigurationsState;
  restrictionProfiles: import('../restrictions-profile/restrictions-profile.state').RestrictionsProfileState;
}
